/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */
/* eslint-disable max-statements */
/* eslint-disable no-await-in-loop */

import AssertionError from 'assertion-error';

import PromiseUtil from '../../promise/promise-util';
import ViewerDelayerFactory from '../../viewer/delayer/viewer-delayer-factory';
import { BaseContentController } from './base-content-controller';
import { isLeafContentController } from './content-controller-util';
import { RealtimePlaylistSimulator } from './playlist-simulator';

/**
 * @import { Time } from '@ki-wi/deterministic'
 * @import { ContentController, ContentControllerFactory, ContentControllerType, Spot, SpotContent } from '.'
 * @import { Content } from '../content'
 * @import { ContentInfo } from '../../content/info/content-info'
 * @import { Logger } from '../../logger/logger'
 * @import UnixTimestamp from '../../unix-timestamp/unix-timestamp'
 * @import ViewerDelayer from '../../viewer/delayer/viewer-delayer'
 * @import { Playlist } from '../../playlist/playlist'
 */

/**
 * @typedef {{spot: Spot<SpotContent | null>; contentController: ContentController}} RealtimeContent
 * @typedef {{content: RealtimeContent; preloaded: boolean;}} NextRealtimeContent
 */

/**
 * @implements {ContentController}
 */
export class PlaylistContentController extends BaseContentController {
  /**
   * @param {ContentControllerFactory} contentControllerFactory
   * @param {ContentInfo} contentInfo
   * @param {Content} content
   * @param {boolean} disableAutorewind
   * @param {Logger} logger
   * @param {HTMLElement} parentEl
   * @param {number} parentVolume
   * @param {UnixTimestamp} unixTimestamp
   */
  constructor(
    contentControllerFactory,
    contentInfo,
    content,
    disableAutorewind,
    logger,
    parentEl,
    parentVolume,
    unixTimestamp
  ) {
    super();
    this.contentControllerFactory = contentControllerFactory;
    this.contentInfo = contentInfo;
    this.disableAutorewind = disableAutorewind;
    this.logger = logger;
    this.parentEl = parentEl;
    this.parentVolume = parentVolume;
    this.unixTimestamp = unixTimestamp;

    /** @type {ContentControllerType} */
    this.type = 'playlist';

    /** @type {Playlist | undefined} */
    const playlist = content.playlist;
    if (!playlist || playlist.deterministic) {
      throw new Error('content should be non deterministic playlist');
    }
    this.playlist = playlist;

    /** @type {AbortController | undefined} */
    this.abortController = undefined;
    /** @type {ViewerDelayer} */
    this.delayer = new ViewerDelayerFactory({
      delay: 1000,
      limit: 5
    }).create();
    /** @type {RealtimeContent | undefined} */
    this.currentRealtimeContent = undefined;
    /** @type {RealtimeContent | undefined} */
    this.nextRealtimeContent = undefined;
    /** @type {(() => void) | undefined} */
    this.removePictogram = undefined;
    this.loopQueue = Promise.resolve();

    /** @type {RealtimePlaylistSimulator} */
    this.simulator = this.createSimulator();

    /** @type {Spot | undefined} */
    this.preloadedSpot = undefined;

    this.now = this.now.bind(this);
  }

  /**
   * @param {SpotContent | null} content
   * @param {number} duration
   * @param {number} volume
   * @returns {ContentController}
   */
  createContentController(content, duration, volume) {
    const model = content?.model ?? null;
    return this.contentControllerFactory.create(this.parentEl, model, {
      contentInfo: this.contentInfo,
      volume,
      trackDuration: duration
    });
  }

  /**
   * @returns {RealtimePlaylistSimulator}
   */
  createSimulator() {
    return new RealtimePlaylistSimulator(
      this.disableAutorewind,
      () => /** @type {Time} */ (new Date().getTime() * 1000),
      this.playlist,
      this.parentVolume,
      this.unixTimestamp
    );
  }

  /**
   * Vrací další přehrávaný obsah v pořadí. Pokud se nextRealtimeContent shoduje
   * s aktuálně získaným obsahem ze simulátoru, tak je vrácen s preloaded === true.
   * Jinak se vrací obsah ze simulátoru s preloaded === false. Null je vráceno pokud
   * byl vrácen prázdný obsah. Undefined je vráceno, pokud playlist dorazil na konec.
   * @param {RealtimeContent | undefined} nextRealtimeContent
   * @returns {Promise<NextRealtimeContent | null | undefined>}
   */
  async getNextRealtimeContent(nextRealtimeContent) {
    /** @type {Spot<SpotContent | null> | undefined} */
    const s = await this.simulator.next();
    if (s === undefined || (s.content === null && s.duration === 0)) {
      // Konec simulačního času nebo konec jedné iterace playlistu.
      return undefined;
    }
    if (s.content === null) {
      // Prázdný obsah.
      return null;
    }
    /**
     * @param {Spot<SpotContent | null>} spot
     * @returns {NextRealtimeContent}
     */
    const newNextRealtimeContent = (spot) => {
      const { content, duration, volume } = spot;
      const contentController = this.createContentController(
        content,
        duration / 1000,
        volume
      );
      return { content: { spot, contentController }, preloaded: false };
    };

    if (!nextRealtimeContent) {
      return newNextRealtimeContent(s);
    }

    const m1 = nextRealtimeContent.spot.content?.model;
    const m2 = s.content.model;
    // Mám preloadovaný content, nutné ověřit jestli byl preload správný.
    if (isSameContent(m1, m2)) {
      const { contentController, spot } = nextRealtimeContent;
      return { content: { contentController, spot }, preloaded: true };
    }
    // Preload by špatný. Unloadni preloadnutý obsah.
    // Umyslné ignorování chyb.
    await nextRealtimeContent.contentController.stop().catch(() => {});
    // Vrať správný obsah.
    return newNextRealtimeContent(s);
  }

  /** @type {ContentController['preload']} */
  async preload() {
    await super.preloadCallback(async () => {
      // TODO: V době preloadu nevíme, co by se mělo přednačíst. Možná předávat
      // preload offset (délka aktuálně přehrávaného media), a toto medium přednačíst.
      this.abortController = await new AbortController();
      this.loopQueue = Promise.resolve();
      const spot = await this.simulator.peek();
      if (!spot || !spot.content) {
        return;
      }
      const contentController = this.createContentController(
        spot.content,
        spot.duration / 1000,
        spot.volume
      );
      try {
        await contentController.preload();
        this.nextRealtimeContent = { spot, contentController };
        // Umýslné ignorování error, protože např. nemusí být
        // medium stažené, takže preload neuspěje.
      } catch {}
    });
  }

  /** @type {ContentController['restart']} */
  async restart() {
    await super.restartCallback(async () => {
      this.abortController = await new AbortController();
      this.loopQueue = Promise.resolve();
      this.simulator = await this.createSimulator();
      this.startPlaylistLoop(this.abortController);
    });
  }

  /**
   * @param {AbortController} abortController
   */
  startPlaylistLoop(abortController) {
    (async () => {
      while (!abortController.signal.aborted) {
        try {
          this.loopQueue = this.startPlaylist(abortController).then((success) =>
            success || abortController.signal.aborted
              ? this.delayer.succeed()
              : this.delayer.fail()
          );
          await this.loopQueue;
        } catch (err) {
          const error = new Error(
            `startPlaylistLoop ${/** @type {Error} */ (err).toString()}`
          );
          this.logger.error(error);
          await this.delayer.fail();
          this.loopQueue = Promise.resolve();
        }
      }
      this.emitter.emit('ended');
    })();
  }

  /** @type {ContentController['start']} */
  async start() {
    const { abortController } = this;
    if (!abortController) {
      throw new AssertionError('AbortController not set');
    }

    // eslint-disable-next-line require-await
    await super.startCallback(async () => {
      // Spuštění asynchronně přehrávací smyčky.
      this.startPlaylistLoop(abortController);
    });
  }

  /**
   * Spustí jeden track playlistu a počká na jeho přehrání. Funkce vrací true,
   * pokud bylo přehrání úspěšné, jinak se vrací false.
   * @param {AbortController} abortController
   * @returns {Promise<boolean>}
   */
  async startPlaylist(abortController) {
    /** @type {RealtimeContent | undefined} */
    let realtimeContent;
    let hasPreloadedContent = false;
    try {
      const next = await this.getNextRealtimeContent(this.nextRealtimeContent);
      if (next === undefined) {
        // Nastal konec playlistu dosažením na konec simulačního času nebo skončila jedna iterace.
        abortController.abort();
        return false;
      }
      if (next === null) {
        // Prázdný obsah.
        return false;
      }
      const { content, preloaded } = next;
      realtimeContent = content;
      hasPreloadedContent = preloaded;
    } catch {
      return false;
    }
    this.nextRealtimeContent = undefined;

    const { contentController, spot } = realtimeContent;
    const abortSignal = abortController.signal;

    /** @type {PromiseWithResolvers<boolean>} */
    const { promise: promiseError, resolve: resolveError } =
      Promise.withResolvers();
    /** @type {PromiseWithResolvers<boolean>} */
    const { promise: promiseEnded, resolve: resolveEnded } =
      Promise.withResolvers();
    /** @type {PromiseWithResolvers<boolean>} */
    const { promise: promiseAbort, resolve: resolveAborted } =
      Promise.withResolvers();

    const onAbort = () => resolveAborted(false);
    const onError = () => resolveError(false);
    const onEnded = () => resolveEnded(true);
    /** @type {(content: Content) => void} */
    const onPlayed = (content) => {
      if (isLeafContentController(contentController)) {
        return;
      }
      // Emitování played eventů pouze pro nekoncové ContentController.
      this.emitter.emit('played', content);
    };
    /** @type {(content: Content) => void} */
    const onPlaying = (content) => {
      if (isLeafContentController(contentController)) {
        return;
      }
      // Emitování playing eventů pouze pro nekoncové ContentController.
      this.emitter.emit('playing', content);
    };

    abortSignal.addEventListener('abort', onAbort);
    contentController.on('error', onError);
    contentController.on('ended', onEnded);
    contentController.on('played', onPlayed);
    contentController.on('playing', onPlaying);

    /** @type {ReturnType<setTimeout> | undefined} */
    let trackTimerId;
    /** @type {Promise<boolean>} */
    const promiseTimer = new Promise((resolve) => {
      if (spot.duration !== 0) {
        trackTimerId = setTimeout(() => resolve(true), spot.duration / 1000);
      }
    });

    try {
      if (!hasPreloadedContent) {
        await contentController.preload();
      }
      await contentController.start(0);
      if (spot.content?.model) {
        this.emitter.emit('playing', spot.content.model);
      }
    } catch (err) {
      // Případné chyby se ignorují.
      return false;
    }

    // Přeskočení preloadu v případech, pokud hraje např. deterministický playlist, šablona.
    // V těchto případech není známý obsah "uvnitř" a není žádoucí spotřebovat omezené zdroje (např. video element) na preload.
    if (isLeafContentController(contentController)) {
      // Přednačtení dalšího media v pořadí.
      /** @type {Spot | undefined} */
      let nextSpot;

      // Délka zpoždění byla zjištěná experimentálně na TV. Zpoždění jen a pouze kvůli WebOS 4.0 a 4.1,
      // kde docházelo k černání videí po kterých následoval obrázek. Zvuk videí na pozadí hrál, fungovalo onended.
      // Jediné, co dokázalo zčernání videa opravit bylo kliknutí na libovolný element v DOM přes DevTools.
      // Zpoždění teoreticky stačí 100 ms, ale po vzoru zaniklého ViewerThred, necháme 200 ms.
      await PromiseUtil.wait(200);
      try {
        nextSpot = await this.simulator.peek();
        if (nextSpot && nextSpot.content) {
          const nextContentController = this.createContentController(
            nextSpot.content,
            nextSpot.duration / 1000,
            nextSpot.volume
          );

          await nextContentController.preload();

          // Musí se nastavit až po preload, protože to může neuspět.
          this.nextRealtimeContent = {
            spot: nextSpot,
            contentController: nextContentController
          };
        }
      } catch (err) {
        // Preload se nepovedl, např. kvůli vyčerpání aktuálně vypůjčených zdrojů.
      }
    }

    return await Promise.race([
      // Časovač podle délky tracku.
      promiseTimer,
      // Reakce na error. Pokud nastane chyba v přehrávání obsahu, tak přerušujeme přehrvání.
      promiseError,
      // Reakce na ended event. Pokud je fyzická délka obsahu kratší než délka tracku.
      promiseEnded,
      // Reakce na vynucené ukončení přehrvání obsahu.
      promiseAbort
    ]).finally(async () => {
      if (spot.content?.model) {
        this.emitter.emit('played', spot.content.model);
      }
      // Umyslné ignorování chyb.
      await contentController.stop().catch(() => {});
      clearTimeout(trackTimerId);
      abortSignal.removeEventListener('abort', onAbort);
      contentController.off('error', onError);
      contentController.off('ended', onEnded);
      contentController.off('played', onPlayed);
      contentController.off('playing', onPlaying);
    });
  }

  /** @type {ContentController['stop']} */
  async stop() {
    try {
      await super.stopCallback(async () => {
        this.abortController?.abort();
        // Prvně se musí počkat na dokončení přehrávací smyčky.
        await this.loopQueue.catch(() => {});

        this.delayer.destroy();
        await this.currentRealtimeContent?.contentController
          .stop()
          .catch(() => {});
        await this.nextRealtimeContent?.contentController
          .stop()
          .catch(() => {});

        this.currentRealtimeContent = undefined;
        this.nextRealtimeContent = undefined;
        this.loopQueue = Promise.resolve();
      });
    } catch {
      // Úmyslené ignorování chyby. Pokud chyba vznikla při stop,
      // tak aby metoda stop byla přerušena. Ten případ by neměl nastávat.
    }
  }

  /**
   * Zjistí aktuální čas.
   * @private
   * @returns {number} Aktuální čas v mikrosekundách.
   */
  now() {
    return this.unixTimestamp.now() * 1000;
  }
}

/**
 * @param {Content | undefined} content1
 * @param {Content | undefined} content2
 * @returns {boolean}
 */
function isSameContent(content1, content2) {
  if (!content1 || !content2) {
    return false;
  }
  if (content1.medium && content2.medium) {
    return content1.medium.id === content2.medium.id;
  }

  if (content1.playlist && content2.playlist) {
    return content1.playlist.id === content2.playlist.id;
  }

  if (content1.layout && content2.layout) {
    return content1.layout.id === content2.layout.id;
  }

  return false;
}
